import { NestAPI } from '@marketing-milk/frontend'
import { AdCreative, CreativeCopyPatch } from '@marketing-milk/interfaces'
import { InfiniteData, QueryKey, useMutation, useQueryClient } from 'react-query'
import { invalidateCreativePreview } from 'hooks/creatives/fetch-creative-preview'
import { creativeCopyEditsQueryKey } from './useCreativeCopyEdits'

export interface CreativeCopyEdit {
  creativeID: number
  patch: CreativeCopyPatch
}

/**
 * Saves edited ad content from the review surface.
 *
 * Employee-only — the page withholds the capability from clients and the endpoint
 * requires builder access — so this can invalidate the builder-only creative
 * consumers unconditionally, unlike the verdict mutation beside it.
 *
 * The endpoint returns the saved creative, and that response is written straight
 * into every list the editor can be opened from — the sprint's creatives, the
 * creative library's pages, and the by-id lookup a linked ad opens from: a card
 * this save created has an id the browser cannot derive, and waiting for a
 * refetch to learn it would leave the editor able to add the same card twice. The
 * invalidations below still run, for the other screens showing this ad.
 */
export const useUpdateCreativeCopy = (businessID: number) => {
  const queryClient = useQueryClient()

  return useMutation(
    async ({ creativeID, patch }: CreativeCopyEdit): Promise<AdCreative> => {
      const response = await NestAPI.patch<AdCreative>(
        `businesses/${businessID}/ad-creatives/${creativeID}/copy`,
        patch
      )
      return response.data
    },
    {
      onSuccess: (saved, { creativeID }) => {
        const replace = (creatives: AdCreative[]) =>
          creatives.map(creative => (creative.id === creativeID ? saved : creative))
        queryClient.setQueriesData<AdCreative[] | undefined>(
          ['sprints', 'sprint-creatives'],
          creatives => creatives && replace(creatives)
        )
        queryClient.setQueriesData<AdCreative[] | undefined>(
          ['creatives-by-ids'],
          creatives => creatives && replace(creatives)
        )
        queryClient.setQueriesData<InfiniteData<{ creatives: AdCreative[] }> | undefined>(
          ['creatives'],
          data =>
            data && {
              ...data,
              pages: data.pages.map(page => ({ ...page, creatives: replace(page.creatives) })),
            }
        )

        // Facebook's own rendering of the ad is cached per tab, outside react-query,
        // and this save is in the same tab as the creative library and campaign
        // screens that display it — so without this they show the pre-edit ad until
        // a reload. The packet surfaces draw their own ads and are unaffected.
        invalidateCreativePreview(businessID, creativeID)

        // Deliberately neither awaited nor allowed to reject: the save is already
        // confirmed, and a refetch that fails afterwards must not turn it into a
        // failed mutation the editor offers to resend.
        //
        // The history row is the one that matters here — the save writes it, so
        // the review surface's history column reads as if the edit was never
        // recorded until this lands.
        const refresh = (key: QueryKey) => queryClient.invalidateQueries(key).catch(() => undefined)

        refresh(creativeCopyEditsQueryKey(businessID, creativeID))
        refresh(['sprints', 'sprint-creatives'])
        refresh(['creatives'])
        refresh(['creatives-by-ids'])
      },
    }
  )
}
